import { useEffect, useRef, useState } from 'react';
import { authFetch } from '../../utils/auth';

/**
 * GET /api/admin/metrics on mount, then re-poll every `intervalMs` (60 s).
 *
 * Contract:
 *   data        — last GOOD response body (null until the first success)
 *   error       — error from the most recent attempt, or null after a success
 *   lastUpdated — Date of the last successful fetch (drives "sist oppdatert")
 *   forbidden   — true once the backend answered 403. There is no frontend
 *                 admin flag; the 403 IS the admin signal, and polling stops.
 *   loading     — true until the first attempt has settled either way
 *
 * A failed poll keeps the previous data and simply waits for the next tick —
 * no retries, no backoff, no storm. Polling is a setTimeout chain (not
 * setInterval) so a slow response can never overlap the next request.
 *
 * The only network call this feature makes; there is no websocket and no
 * localStorage — the wall screen must be able to run for hours untouched.
 */
export const POLL_INTERVAL_MS = 60 * 1000;
export const METRICS_PATH = '/admin/metrics';

const INITIAL = { data: null, error: null, lastUpdated: null, forbidden: false, loading: true };

export default function useAdminMetrics({ intervalMs = POLL_INTERVAL_MS } = {}) {
  const [state, setState] = useState(INITIAL);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const schedule = (fn) => {
      timerRef.current = setTimeout(fn, intervalMs);
    };

    const tick = async () => {
      let forbidden = false;
      try {
        const res = await authFetch(METRICS_PATH, { method: 'GET' });
        const json = await res.json();
        if (cancelled) return;
        setState({ data: json, error: null, lastUpdated: new Date(), forbidden: false, loading: false });
      } catch (err) {
        if (cancelled) return;
        forbidden = err?.status === 403;
        setState(prev => ({ ...prev, error: err, forbidden, loading: false }));
      }
      if (!cancelled && !forbidden) schedule(tick);
    };

    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [intervalMs]);

  return state;
}
